/**
 * TubeNode - Create a tube along a 3D path
 *
 * Define 3D points, creates a tube geometry following the curve.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';

const tubeEndpoints = {
  ...internals({
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  }),
  ...params({
    // Default: simple helix - array of [x, y, z] triplets
    points: {
      dataType: 'array',
      value: [[0, 0, 0], [1, 0.5, 0], [0, 1, 0], [-1, 1.5, 0], [0, 2, 0]],
      label: 'Path Points'
    },
    radius: { value: 0.1, min: 0.01, max: 2, step: 0.01 },
    tubularSegments: { value: 64, min: 3, max: 256, step: 1, label: 'Path Segments' },
    radialSegments: { value: 8, min: 3, max: 32, step: 1, label: 'Radial Segments' },
    closed: { dataType: 'boolean', value: false }
  })
};

class TubeNode extends BaseNode<typeof tubeEndpoints> {
  private three: Three;
  /** Stored mesh reference from SceneNode — for in-place geometry replacement */
  private mesh: any = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  /** Build geometry from current params — called by SceneNode */
  buildGeometry(): any {
    return this.generateGeometryResult();
  }

  /** Store mesh reference from SceneNode for in-place param mutation */
  setThreeObject(mesh: any): void {
    this.mesh = mesh;
  }

  /** Per-frame callback for useExp params — rebuilds geometry each frame */
  animateCallback = () => {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  };

  initialize(): void {
    super.initialize();
  }

  private parsePoints(): any[] {
    const arr = this.params.points.value;
    if (!Array.isArray(arr)) {
      throw new Error('TubeNode: points must be an array of [x, y, z] triplets');
    }
    return arr
      .filter((p: any) => Array.isArray(p) && p.length >= 3)
      .map((p: any) => new this.three.Vector3(Number(p[0]), Number(p[1]), Number(p[2])));
  }

  private generateGeometryResult(): any {
    const points = this.parsePoints();
    if (points.length < 2) {
      return null;
    }

    // Create smooth curve through points
    const curve = new this.three.CatmullRomCurve3(points, this.params.closed.value);

    const geometry = new this.three.TubeGeometry(
      curve,
      this.params.tubularSegments.value,
      this.params.radius.value,
      this.params.radialSegments.value,
      this.params.closed.value
    );

    geometry.computeVertexNormals();
    return geometry;
  }

  onParamChanged(): void {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  }
}

export const tubeNodeDef: NodeDef<TubeNode> = {
  type: 'Tube',
  shortName: 'tube',
  info: 'Creates a tube geometry following a 3D path defined by waypoints.',
  category: 'shape',
  endpoints: tubeEndpoints,
  ctor: TubeNode
};
